import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { match, none, Option, some } from '../../../../shared/functions/functional';
import { FieldTree, FormField, SchemaPath, SchemaPathTree } from '@angular/forms/signals';
import { InDto, MceApiService, RCBO_IEC61009Dto } from '../../../../core/mceapi.client.generated';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { rxResource } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { Load } from '../../load/load.model';

export interface RCBOIEC61009 {
  assemblyGuid: string,
  rating: number,
  autoRating: boolean,
}

export function buildRCBOIEC61009Schema(path: SchemaPathTree<RCBOIEC61009>) { }

export function createRCBOIEC61009(dto: Option<RCBO_IEC61009Dto>) {
  return signal<RCBOIEC61009>(
    match(
      () => ({ assemblyGuid: '', rating: 0, autoRating: true }),
      (x: RCBO_IEC61009Dto): RCBOIEC61009 => {
        if (x.assemblyGuid === undefined || x.rating === undefined || x.autoRating === undefined) {
          throw new Error('RCBO DTO is missing required values.');
        }

        return {
          assemblyGuid: x.assemblyGuid,
          rating: x.rating,
          autoRating: x.autoRating
        };
      })(dto));
}

export function createRCBOIEC61009Dto(model: FieldTree<RCBOIEC61009>) {
  return signal<Option<RCBO_IEC61009Dto>>(
    model.assemblyGuid().value() === '' || model.rating().value() === 0
      ? none
      : some({
          assemblyGuid: model.assemblyGuid().value(),
          rating: model.rating().value(),
          autoRating: model.autoRating().value(),
        }));
}


@Component({
  selector: 'protection-rcbo',
  imports: [FormField, MatSelectModule, MatSlideToggleModule],
  templateUrl: './rcbo.component.html',
})
export class RcboComponent {

  readonly form = input.required<FieldTree<RCBOIEC61009>>();
  readonly load = input.required<FieldTree<Load>>();

  private readonly mceApiService = inject(MceApiService);

  framesRx = rxResource({
    params: () => ({}),
    stream: () =>
      this.mceApiService.v1ProtectionFrames('RCBO_IEC61009')
  });
  frames = computed(() => this.framesRx.value() ?? []);

  ratingsRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of([])
        : this.mceApiService.v1ProtectionRatings('RCBO_IEC61009', params.assemblyGuid)
  });
  ratings = computed(() => this.ratingsRx.value() ?? []);

  autoRatingRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value(), ib: this.load().ib().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of({ i: 0 })
        : this.mceApiService.v1ProtectionRatingsAuto('RCBO_IEC61009', params.assemblyGuid, params.ib)
  });
  autoRating = computed<InDto>(() => this.autoRatingRx.value() ?? { i: 0 });

  setAutoRating = effect(() => {
    if (!this.form().autoRating().value()) return;
    const autoRating = this.autoRating().i;
    if (autoRating === undefined || autoRating === 0) return;

    if (autoRating !== this.form().rating().value())
      this.form().rating().value.set(autoRating);
  });


  /// Ensure the frames has a valid selection.
  frameToValid = effect(() => {
    const frameGuid = this.frames()[1]?.value;
    if (frameGuid !== undefined && this.form().assemblyGuid().value() === '')
      this.form().assemblyGuid().value.set(frameGuid);
  });

}
